import React, { useContext } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Icon, Dropdown, Menu, MenuItem } from 'components';
import { AuthContext, UniversalSearchContext } from 'contexts';
import { Button as ButtonV2, Icon as IconV2 } from '@bb/elements-web';
import { featureEnabled } from 'utils';
import { useAnsweringServiceDetails } from 'hooks';
import { AnsweringServiceType } from '@bb/common';

type ListingActionsProps = {
  isDropdown?: boolean;
  /** ID of the user viewing this page */
  userId: string;
};

const ListingActions: React.FC<ListingActionsProps> = ({
  isDropdown = false,
  userId,
}) => {
  const history = useHistory();
  const { auth, hasFeature, hasLimitedAccess, answeringPortalConfig } =
    useContext(AuthContext);
  const { setSearchOpen } = useContext(UniversalSearchContext);
  const isGlobalAdmin = auth?.roles?.[auth?.role] >= auth?.roles?.GLOBAL_ADMIN;
  const isSubscriptionHubEnabled = featureEnabled('subscriptionHubFeature');

  const { answeringServiceType } = useAnsweringServiceDetails({
    viewingLevel: isGlobalAdmin ? 'org' : 'user',
    userId,
    enabled: isSubscriptionHubEnabled,
  });

  const isLegacy = answeringServiceType === AnsweringServiceType.Legacy;

  const isSupraMvpInSpecialRegion =
    hasFeature('SUPRA_MVP') &&
    (auth?.org?.country === 'CAN' || auth?.parentOrgType === 'mlsStaff');
  const canEditListing = featureEnabled('supraHideAdminFunctionalityFeature')
    ? !hasFeature('SUPRA_MVP') || isSupraMvpInSpecialRegion
    : true;

  // Centralized Logic for Actions
  const actionItems = [
    {
      id: 'view',
      show:
        Boolean(auth?.roles) &&
        answeringPortalConfig?.useLiveAppointmentDesk &&
        !isLegacy,
      label: 'View Live Concierge',
      dataCy: 'dashboard-view-live-concierge',
      href: isSubscriptionHubEnabled
        ? isGlobalAdmin
          ? '/#/answering_service/org/'
          : '/#/answering_service/user/'
        : isGlobalAdmin
          ? '/admin/appointment_desk'
          : '/profile?tab=LIVE_CONCIERGE_SERVICE',
    },
    {
      id: 'create',
      show: hasFeature('CREATE_LISTING') && canEditListing,
      label: 'Create Listing',
      href: '/listing/new',
      dataCy: 'dashboard-create-listing',
      disabled: hasLimitedAccess,
    },
    {
      id: 'merge',
      show: canEditListing,
      label: 'Merge/Combine Listing',
      href: '/listing/merge/options',
      dataCy: 'dashboard-combine-listing',
      disabled: hasLimitedAccess,
    },
  ].filter(item => item.show);

  // Helper to handle SPA navigation while allowing browser defaults (like cmd+click)
  const handleNavigate = (e: React.MouseEvent, path: string) => {
    // Let the browser handle modified clicks (cmd/ctrl+click) for new tabs
    if (e.metaKey || e.ctrlKey) return;

    e.preventDefault();
    history.push(path.replace('/#', ''));
  };

  const renderMenu = () => (
    <Menu>
      {actionItems.map(item => (
        <MenuItem key={item.id} data-cy={item.dataCy} disabled={item.disabled}>
          <a
            href={item.href}
            onClick={e => handleNavigate(e as any, item.href)}
            style={{
              color: 'inherit',
              textDecoration: 'none',
              display: 'block',
              width: '100%',
            }}
          >
            {item.label}
          </a>
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <ActionButtonContainer>
      {actionItems.map(item => (
        <StyledActionButton
          type="secondary"
          key={item.id}
          disabled={item.disabled}
          dataCy={item.dataCy}
          href={item.href}
          onClick={e => handleNavigate(e as any, item.href)}
        >
          {item.label}
        </StyledActionButton>
      ))}
    </ActionButtonContainer>
  );
};

const MoreButtonContainer = styled.div`
  text-align: right;
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const ActionButtonContainer = styled.div`
  text-align: right;
  margin-left: auto;
`;

const StyledActionButton = styled(ButtonV2)`
  margin-left: 5px;
`;

export default ListingActions;
